import { HttpClient, HttpEventType } from "@angular/common/http";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { FrontlineOpsService } from "src/app/Services/frontline-ops.service";
import { environment } from "src/environments/environment";
import {
  GUIDE_QUESTION_LIMITS,
  GuideFieldValidation,
  GuideQuestion,
  GuideQuestionAnswer,
  GuideQuestionForm,
  isQuestionAnswered,
  parseGuideQuestionForm,
} from "src/app/models/guide-question-form.model";

/**
 * Renders a question form for a viewer, saves the answers and emits `submitted` once they are stored.
 * Correct answers are never revealed.
 */
@Component({
  selector: "app-guide-question-form",
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatIconModule],
  templateUrl: "./guide-question-form.component.html",
  styleUrls: ["./guide-question-form.component.scss"],
})
export class GuideQuestionFormComponent implements OnChanges {
  @Input() form: GuideQuestionForm | null | undefined;
  @Input() flowId = "";
  @Input() formKey = "";
  @Input() stepIndex: number | null = null;
  @Input() sessionId: string | null = null;
  @Input() flowVersion: number | null = null;
  @Input() submitLabel = "Save and continue";
  @Input() showCancel = false;
  /** The step panel owns Save and continue, so the form only renders the fields. */
  @Input() hideActions = false;
  /** "dialog" adds the popup header (with close button) and footer bar used by other site dialogs. */
  @Input() variant: "inline" | "dialog" = "inline";
  @Output() submitted = new EventEmitter<GuideQuestionAnswer[]>();
  @Output() skipped = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  model: GuideQuestionForm | null = null;
  answers: Record<string, GuideQuestionAnswer> = {};
  saving = false;
  error = "";
  fieldMessages: Record<string, string> = {};
  uploadProgress: Record<string, number> = {};
  readonly maxAnswerLength = GUIDE_QUESTION_LIMITS.maxAnswerTextLength;

  constructor(private frontlineOps: FrontlineOpsService, private http: HttpClient) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["form"] || changes["formKey"]) {
      this.model = parseGuideQuestionForm(this.form);
      this.answers = {};
      this.error = "";
      this.fieldMessages = {};
      this.saving = false;
      (this.model?.questions || []).forEach((q) => {
        this.answers[q.id] = { questionId: q.id, optionIds: [], text: "" };
      });
    }
  }

  get hasRequired(): boolean {
    return (this.model?.questions || []).some((q) => q.required);
  }

  get canSubmit(): boolean {
    if (!this.model || this.saving) return false;
    return this.model.questions.every((q) => !q.required || isQuestionAnswered(q, this.answers[q.id]));
  }

  get hasAnyAnswer(): boolean {
    return (this.model?.questions || []).some((q) => isQuestionAnswered(q, this.answers[q.id]));
  }

  isChosen(q: GuideQuestion, optionId: string): boolean {
    return (this.answers[q.id]?.optionIds || []).includes(optionId);
  }

  choose(q: GuideQuestion, optionId: string): void {
    const a = this.answers[q.id];
    if (!a) return;
    if (q.type === "multiple") {
      const ids = new Set(a.optionIds || []);
      if (ids.has(optionId)) ids.delete(optionId);
      else ids.add(optionId);
      a.optionIds = Array.from(ids);
    } else {
      a.optionIds = [optionId];
    }
    this.error = "";
  }

  submit(): void {
    if (!this.model || this.saving) return;
    if (!this.canSubmit) {
      this.error = "Complete the required fields before continuing.";
      return;
    }
    const answers = this.model.questions
      .map((q) => this.answers[q.id])
      .filter((a, i) => isQuestionAnswered(this.model!.questions[i], a))
      .map((a) => ({
        questionId: a.questionId,
        optionIds: a.optionIds?.length ? a.optionIds : undefined,
        text: a.text?.trim() ? a.text.trim().slice(0, this.maxAnswerLength) : undefined,
        numberValue:
          a.numberValue === null || a.numberValue === undefined || (a.numberValue as any) === ""
            ? undefined
            : Number(a.numberValue),
        photoUrl: a.photoUrl || undefined,
        confirmed: a.confirmed ? true : undefined,
      }));

    if (!this.flowId || !this.formKey) {
      this.submitted.emit(answers);
      return;
    }

    this.saving = true;
    this.error = "";
    this.frontlineOps
      .recordQuestionResponse({
        flowId: this.flowId,
        formKey: this.formKey,
        stepIndex: this.stepIndex,
        sessionId: this.sessionId,
        flowVersion: this.flowVersion,
        answers,
      })
      .subscribe({
        next: (result) => {
          this.saving = false;
          const failed = result?.validationPassed === false || result?.passed === false;
          if (failed) {
            this.applyFieldResults(result?.fieldResults || []);
            this.error = result?.validationMessage || "Correct the highlighted responses before continuing.";
            return;
          }
          this.fieldMessages = {};
          this.submitted.emit(answers);
        },
        error: (err) => {
          this.saving = false;
          this.error = err?.error?.error || "We couldn't save your answers. Please try again.";
        },
      });
  }

  fieldMessage(questionId: string): string {
    return this.fieldMessages[questionId] || "";
  }

  onPhotoSelected(q: GuideQuestion, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      this.fieldMessages[q.id] = "Choose a photo.";
      return;
    }
    const formData = new FormData();
    formData.append("fileData", file);
    this.uploadProgress[q.id] = 0;
    this.http
      .post(environment.apiBaseUrl + environment.uploadImageUrl, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe({
        next: (uploadEvent: any) => {
          if (uploadEvent.type === HttpEventType.UploadProgress && uploadEvent.total) {
            this.uploadProgress[q.id] = Math.round((uploadEvent.loaded / uploadEvent.total) * 100);
          }
          if (uploadEvent.type === HttpEventType.Response) {
            const body = uploadEvent.body as any;
            this.uploadProgress[q.id] = 0;
            if (body?.status && body.fileName) {
              this.answers[q.id].photoUrl = environment.imageStorageBaseUrl + body.fileName;
              this.fieldMessages[q.id] = "";
              this.error = "";
            } else {
              this.fieldMessages[q.id] = body?.message || "The photo could not be uploaded.";
            }
          }
        },
        error: () => {
          this.uploadProgress[q.id] = 0;
          this.fieldMessages[q.id] = "The photo could not be uploaded.";
        },
      });
  }

  clearPhoto(q: GuideQuestion): void {
    if (this.answers[q.id]) this.answers[q.id].photoUrl = "";
  }

  private applyFieldResults(results: GuideFieldValidation[]): void {
    this.fieldMessages = {};
    results.forEach((r) => {
      const id = r.questionId || (r as any).QuestionId;
      const message = r.message || (r as any).Message;
      const passed = r.passed ?? (r as any).Passed;
      if (id && passed === false && message) this.fieldMessages[id] = message;
    });
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }
}
